import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

/**
 * AI State Context - SINGLE SOURCE OF TRUTH
 * 
 * 🔒 IMPORTANT: This context uses the authoritative /api/tenant/ai/effective-state
 * endpoint as the ONLY source for tenant state.
 * 
 * DO NOT derive state from multiple endpoints.
 * DO NOT check hasActiveSubscription separately.
 * DO NOT infer mode from billing status.
 * 
 * Use effective_mode from this context for ALL state decisions.
 */

const AIStateContext = createContext(null);

export const useAIState = () => {
  const context = useContext(AIStateContext);
  if (!context) {
    throw new Error('useAIState must be used within AIStateProvider');
  }
  return context;
};

export const AIStateProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  // Core AI state - derived from SINGLE effective-state endpoint
  const [aiState, setAiState] = useState({
    loading: true,
    initialized: false,
    error: null,
    
    // ========================================
    // 🔒 SINGLE SOURCE OF TRUTH - from /api/tenant/ai/effective-state
    // ========================================
    tenantId: null,
    tenantType: 'TRIAL',        // DEMO | TRIAL | PAID
    executionMode: 'SIMULATED', // User's requested mode
    effectiveMode: 'SIMULATED', // ACTUAL mode being applied (this is what matters!)
    isDemo: true,               // Derived: tenantType !== 'PAID'
    
    // Plan & Billing
    plan: { plan_id: 'free', name: 'Free', billing_status: 'active' },
    billingStatus: 'active',
    
    // Eligibility & Blocking
    canSwitchToLive: false,
    canExecuteAI: true,
    blockedReason: null,
    killSwitchActive: false,
    
    // Metadata
    lastModeChange: null,
    lastModeChangedBy: null,
    
    // ========================================
    // Agent status (separate endpoint, OK to keep)
    // ========================================
    aiEnabled: true,
    // Plan-level AI entitlement (Manual Pro = paid CRM with AI OFF). Fetched for
    // ALL authenticated users (not just admins) so gating works app-wide.
    planAiEnabled: true,
    planName: null,
    aiStatusLoaded: false,
    agents: {
      sales: { enabled: false, paused: false },
      support: { enabled: false, paused: false },
      accounts: { enabled: false, paused: false },
      inventory: { enabled: false, paused: false },
      hr: { enabled: false, paused: false }
    },
    governanceRestrictions: [],
  });

  // Check if user is tenant admin
  const isTenantAdmin = user?.role === 'tenant_admin' || 
                         user?.role === 'superadmin' || 
                         user?.role === 'admin' || 
                         user?.role === 'org_admin';

  const lastFetchRef = useRef(0);

  /**
   * 🔒 AUTHORITATIVE STATE FETCH
   * 
   * This function fetches from the SINGLE source of truth endpoint.
   * ALL state derivation happens on the backend - frontend just renders.
   */
  const fetchEffectiveState = useCallback(async (force = false) => {
    if (!isAuthenticated || !user) {
      setAiState(prev => ({ 
        ...prev, 
        loading: false, 
        initialized: true,
        isDemo: true,
        effectiveMode: 'SIMULATED'
      }));
      return;
    }

    // Non-admin users get default state
    if (!isTenantAdmin) {
      setAiState(prev => ({
        ...prev,
        loading: false,
        initialized: true,
        isDemo: true,
        effectiveMode: 'SIMULATED'
      }));
      return;
    }

    try {
      // Throttle: this callback's identity changes with auth-context re-renders,
      // which re-fires the mount effect and hammered effective-state/agents/safety
      // every ~1s app-wide (starving other XHRs, e.g. the Go-LIVE readiness call,
      // behind the browser's 6-connection cap). One fetch per 15s unless forced.
      const nowTs = Date.now();
      if (!force && nowTs - lastFetchRef.current < 15000) return;
      lastFetchRef.current = nowTs;

      // ========================================
      // 🔒 FETCH SINGLE SOURCE OF TRUTH
      // ========================================
      const [effectiveStateRes, agentsRes, safetyRes] = await Promise.all([
        api.get('/api/tenant/ai/effective-state'),
        api.get('/api/tenant/ai/agents').catch(() => ({ data: [] })),
        api.get('/api/tenant/ai/safety').catch(() => ({ data: {} }))
      ]);

      const effectiveState = effectiveStateRes.data;
      const agents = agentsRes.data || [];
      const safety = safetyRes.data || {};

      // Build agent status map
      const agentMap = {
        sales: { enabled: false, paused: false },
        support: { enabled: false, paused: false },
        accounts: { enabled: false, paused: false },
        inventory: { enabled: false, paused: false },
        hr: { enabled: false, paused: false }
      };

      const agentList = Array.isArray(agents) ? agents : (agents.agents || []);
      
      agentList.forEach(agent => {
        const type = agent.type || agent.agent_type?.toLowerCase()?.replace('_ai', '') || 
                     agent.agent_name?.toLowerCase()?.split('agent')[0]?.replace('playbook', '');
        
        if (type && agentMap[type] !== undefined) {
          agentMap[type] = {
            enabled: agent.enabled !== false,
            paused: agent.status === 'paused' || agent.auto_paused === true
          };
        }
      });

      // ========================================
      // SET STATE FROM SINGLE SOURCE OF TRUTH
      // ========================================
      setAiState(prev => ({
        ...prev,
        loading: false,
        initialized: true,
        error: null,
        
        // 🔒 From effective-state endpoint (AUTHORITATIVE)
        tenantId: effectiveState.tenant_id,
        tenantType: effectiveState.tenant_type,
        executionMode: effectiveState.execution_mode,
        effectiveMode: effectiveState.effective_mode, // THIS IS THE KEY VALUE
        isDemo: effectiveState.is_demo,
        
        plan: effectiveState.plan,
        billingStatus: effectiveState.billing_status,
        
        canSwitchToLive: effectiveState.can_switch_to_live,
        canExecuteAI: effectiveState.can_execute_ai,
        blockedReason: effectiveState.blocked_reason,
        killSwitchActive: effectiveState.kill_switch_active,
        
        lastModeChange: effectiveState.last_mode_change,
        lastModeChangedBy: effectiveState.last_mode_changed_by,
        
        // Agent status (from agents endpoint)
        aiEnabled: safety.ai_enabled !== false,
        agents: agentMap,
        governanceRestrictions: safety.governance_restrictions || [],
      }));

      console.log('[AIStateContext] ✅ Loaded from effective-state:', {
        tenantType: effectiveState.tenant_type,
        effectiveMode: effectiveState.effective_mode,
        canSwitchToLive: effectiveState.can_switch_to_live,
        isDemo: effectiveState.is_demo
      });

    } catch (error) {
      console.error('[AIStateContext] ❌ Failed to fetch effective state:', error);
      setAiState(prev => ({
        ...prev,
        loading: false,
        initialized: true,
        error: error.message
      }));
    }
  }, [isAuthenticated, user, isTenantAdmin]);

  /**
   * 🔄 REFRESH STATE
   * 
   * Call this after:
   * - Login
   * - LIVE mode toggle
   * - Subscription change
   * - Any state-affecting action
   */
  const refreshAIState = useCallback(async () => {
    console.log('[AIStateContext] 🔄 Refreshing state...');
    setAiState(prev => ({ ...prev, loading: true }));
    await fetchEffectiveState(true);
  }, [fetchEffectiveState]);

  /**
   * 🔄 FORCE STATE SYNC
   * 
   * Call this when state corruption is suspected.
   * Uses the force-state-sync endpoint to fix any mismatches.
   */
  const forceStateSync = useCallback(async () => {
    console.log('[AIStateContext] 🛠️ Force syncing state...');
    setAiState(prev => ({ ...prev, loading: true }));
    
    try {
      // Call force-sync endpoint
      const response = await api.post('/api/tenant/ai/force-state-sync');
      const effectiveState = response.data;
      
      setAiState(prev => ({
        ...prev,
        loading: false,
        tenantId: effectiveState.tenant_id,
        tenantType: effectiveState.tenant_type,
        executionMode: effectiveState.execution_mode,
        effectiveMode: effectiveState.effective_mode,
        isDemo: effectiveState.is_demo,
        plan: effectiveState.plan,
        billingStatus: effectiveState.billing_status,
        canSwitchToLive: effectiveState.can_switch_to_live,
        canExecuteAI: effectiveState.can_execute_ai,
        blockedReason: effectiveState.blocked_reason,
        killSwitchActive: effectiveState.kill_switch_active,
      }));
      
      console.log('[AIStateContext] ✅ Force sync complete');
      return effectiveState;
    } catch (error) {
      console.error('[AIStateContext] ❌ Force sync failed:', error);
      setAiState(prev => ({ ...prev, loading: false }));
      throw error;
    }
  }, []);

  // Check if specific agent can execute
  const canExecuteAgent = useCallback((agentType) => {
    const agent = aiState.agents[agentType];
    if (!agent) return { allowed: false, reason: 'Agent not found' };
    
    if (!aiState.aiEnabled) {
      return { allowed: false, reason: 'AI is disabled for this tenant' };
    }
    
    if (aiState.killSwitchActive) {
      return { allowed: false, reason: 'Kill switch is active' };
    }
    
    if (!agent.enabled) {
      return { allowed: false, reason: `${agentType} AI is not enabled` };
    }
    
    if (agent.paused) {
      return { allowed: false, reason: `${agentType} AI is paused` };
    }
    
    return { allowed: true, reason: null };
  }, [aiState]);

  /**
   * 🔒 GET EFFECTIVE MODE
   * 
   * Use this for ALL mode checks in the UI.
   * DO NOT check executionMode directly - always use effectiveMode.
   */
  const getExecutionMode = useCallback(() => {
    return aiState.effectiveMode || 'SIMULATED';
  }, [aiState.effectiveMode]);

  /**
   * 🔒 CHECK IF LIVE MODE
   * 
   * Single authoritative check for LIVE mode.
   */
  const isLiveMode = useCallback(() => {
    return aiState.effectiveMode === 'LIVE';
  }, [aiState.effectiveMode]);

  /**
   * 🔒 CHECK IF CAN GO LIVE
   * 
   * Use this to determine if LIVE toggle should be enabled.
   */
  const canGoLive = useCallback(() => {
    return aiState.canSwitchToLive && !aiState.killSwitchActive;
  }, [aiState.canSwitchToLive, aiState.killSwitchActive]);

  // Initial fetch on auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchEffectiveState();
    } else {
      setAiState(prev => ({
        ...prev,
        loading: false,
        initialized: true,
        isDemo: true,
        effectiveMode: 'SIMULATED'
      }));
    }
  }, [isAuthenticated, fetchEffectiveState]);

  // Plan-level AI entitlement — fetched for EVERY authenticated user (drives the
  // app-wide "Upgrade to unlock AI" gate for Manual Pro). Lightweight + cached.
  useEffect(() => {
    if (!isAuthenticated) {
      setAiState(prev => ({ ...prev, planAiEnabled: true, aiStatusLoaded: true }));
      return;
    }
    let cancelled = false;
    api.get('/api/tenant/billing/ai-status')
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        setAiState(prev => ({
          ...prev,
          planAiEnabled: d.ai_enabled !== false,
          planName: d.plan_name || prev.planName,
          aiStatusLoaded: true,
        }));
      })
      .catch(() => { if (!cancelled) setAiState(prev => ({ ...prev, aiStatusLoaded: true })); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const value = {
    ...aiState,
    isTenantAdmin,
    
    // 🔒 Core functions
    refreshAIState,
    forceStateSync,
    
    // Helper functions
    canExecuteAgent,
    getExecutionMode,
    isLiveMode,
    canGoLive,
  };

  return (
    <AIStateContext.Provider value={value}>
      {children}
    </AIStateContext.Provider>
  );
};

export default AIStateContext;
